import React, { memo } from "react";
import { ColumnDef } from "types/admin";

interface ResponsiveTableProps<T = any> {
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  /** Stable key per row. Falls back to `item.id` then the index. */
  rowKey?: (item: T, index: number) => string | number;
  /** Header text for the trailing actions column (blank by default). */
  actionsHeader?: string;
  renderActions?: (item: T, index: number) => React.ReactNode;
  className?: string;
}

/**
 * Reusable Responsive Table
 *
 * Renders a normal table from `md` up and a stacked label/value card list below
 * it, so wide admin tables stay readable on phones instead of forcing a
 * horizontal scroll. Column definitions are shared between both layouts.
 */
function ResponsiveTableInner<T = any>({
  columns,
  data,
  loading = false,
  emptyMessage = "No records found",
  rowKey,
  actionsHeader = "",
  renderActions,
  className = "",
}: ResponsiveTableProps<T>) {
  const keyFor = (item: T, index: number) =>
    rowKey ? rowKey(item, index) : ((item as any)?.id ?? index);

  const valueFor = (item: T, column: ColumnDef<T>, index: number) =>
    column.render
      ? column.render(item, index)
      : ((item as any)[column.key] ?? "-");

  if (loading) {
    return (
      <div className={className}>
        <p className="text-center py-8 text-lightgray-200 text-sm">Loading...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={className}>
        <p className="text-center py-24 text-lightgray-200 text-sm">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Desktop / tablet: table */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-[#E5E7EB]">
        <table className="w-full text-sm text-left">
          <thead className="bg-[#F9FAFB] text-grease-100">
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  scope="col"
                  className={`px-6 py-4 font-semibold whitespace-nowrap ${
                    column.className || ""
                  }`}
                >
                  {column.header}
                </th>
              ))}
              {renderActions && (
                <th
                  scope="col"
                  className="px-6 py-4 font-semibold text-right whitespace-nowrap"
                >
                  {actionsHeader}
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {data.map((item, index) => (
              <tr
                key={keyFor(item, index)}
                className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
              >
                {columns.map((column) => (
                  <td
                    key={String(column.key)}
                    className={`px-6 py-4 text-grease-200 align-middle ${
                      column.className || ""
                    }`}
                  >
                    {valueFor(item, column, index)}
                  </td>
                ))}
                {renderActions && (
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {renderActions(item, index)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards */}
      <div className="md:hidden space-y-3">
        {data.map((item, index) => (
          <div
            key={keyFor(item, index)}
            className="rounded-xl border border-[#E5E7EB] p-4"
          >
            <dl className="space-y-2.5">
              {columns.map((column) => (
                <div
                  key={String(column.key)}
                  className="flex items-start justify-between gap-4"
                >
                  <dt className="text-xs font-medium text-lightgray-200 shrink-0">
                    {column.header}
                  </dt>
                  <dd className="text-sm text-grease-200 text-right min-w-0 break-words">
                    {valueFor(item, column, index)}
                  </dd>
                </div>
              ))}
            </dl>

            {renderActions && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-[#F3F4F6]">
                {renderActions(item, index)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const ResponsiveTable = memo(
  ResponsiveTableInner,
) as typeof ResponsiveTableInner;

export default ResponsiveTable;
